import React, { memo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Car,
  LogIn,
  LayoutDashboard,
  User,
  LogOut,
  MessageSquare,
  Bell,
  Calendar,
  Clipboard,
  Search,
  DollarSign,
  Building2,
  UserPlus,
  XCircle,
  HelpCircle,
  MessageCircle,
  Info,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { getDisplayName, cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CurrencySelector } from "@/components/currency/CurrencySelector";
import { ADMIN_NAV_ITEMS, readAdminScopeFromUser, scopeIsAllowed } from "@/lib/admin/adminScope";
import { adminNavIcon } from "@/lib/admin/adminNavIcons";

interface MobileMenuProps {
  user: any;
  isOpen: boolean;
  onClose: () => void;
  onSignOut: () => void;
  getInitials: (user: any) => string;
  unreadCount?: number;
  onBecomeOwner: (e: React.MouseEvent) => void;
}

export const MobileMenu = memo(
  ({
    user,
    isOpen,
    onClose,
    onSignOut,
    getInitials,
    unreadCount = 0,
    onBecomeOwner,
  }: MobileMenuProps) => {
    const { t } = useTranslation();
    const { getUserRole } = useAuth();

    const [userRole, setUserRole] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [profile, setProfile] = useState<{
      role?: string;
      agency_name?: string | null;
      first_name?: string | null;
      last_name?: string | null;
    } | null>(null);

    useEffect(() => {
      const fetchUserData = async () => {
        if (user) {
          try {
            const role = await getUserRole();
            setUserRole(role);

            if (user?.id) {
              const { data: profileData, error } = await supabase
                .from("profiles")
                .select("role, agency_name, first_name, last_name")
                .eq("id", user.id)
                .single();

              if (!error && profileData) {
                setProfile(profileData);
              }
            }
          } catch (error) {
            console.error("Erreur lors de la récupération des données utilisateur:", error);
          } finally {
            setIsLoading(false);
          }
        }
      };

      fetchUserData();
    }, [user, getUserRole]);

    const isOwner = userRole === "owner";
    const isRenter = userRole === "renter";
    const isAdmin = userRole === "admin";
    const adminScope = readAdminScopeFromUser(user);
    const visibleAdminNav = ADMIN_NAV_ITEMS.filter((item) => scopeIsAllowed(adminScope, item.scopes));

    const getRoleBadgeColor = () => {
      if (isOwner) return "bg-blue-100 text-blue-800";
      if (isRenter) return "bg-green-100 text-green-800";
      if (isAdmin) return "bg-purple-100 text-purple-800";
      return "bg-gray-100 text-gray-800";
    };

    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[320px] sm:w-[380px] p-0 bg-white">
          <div className="flex flex-col h-full">
            {/* Header avec gradient */}
            <SheetHeader className="px-5 py-5 border-b bg-gradient-to-br from-green-night/5 via-emerald-soft/10 to-transparent">
              <SheetTitle className="text-xl font-bold text-gray-900">{t('common.menu')}</SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto">
              {user ? (
                <div className="flex flex-col">
                  {/* Profil utilisateur amélioré */}
                  <div className="px-5 py-5 bg-gradient-to-br from-emerald-soft/20 via-green-sky/10 to-transparent border-b border-gray-100">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12 border-2 border-white shadow-md ring-2 ring-green-night/10">
                        <AvatarImage
                          src={user.user_metadata?.avatar_url}
                          alt={profile ? getDisplayName(profile) : user.user_metadata?.first_name}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-gradient-to-br from-green-night to-green-light text-white font-bold">
                          {profile ? getInitials(profile) : getInitials(user)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col flex-1 min-w-0">
                        <p className="font-semibold text-base text-gray-900 truncate">
                          {profile
                            ? getDisplayName(profile)
                            : user.user_metadata?.first_name && user.user_metadata?.last_name
                              ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
                              : user.email?.split("@")[0] || t('common.user')}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{user.email}</p>
                        {!isLoading && userRole && (
                          <Badge className={`mt-2 text-xs w-fit shadow-sm ${getRoleBadgeColor()}`}>
                            {userRole === "owner"
                              ? t('common.owner')
                              : userRole === "renter"
                                ? t('common.renter')
                                : userRole === "admin"
                                  ? t('common.admin')
                                  : t('common.user')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Navigation principale avec style amélioré */}
                  <div className="px-3 py-4 space-y-2">
                    {/* Options spécifiques au rôle */}
                    {isOwner && (
                      <>
                        <Link to="/dashboard/owner" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <LayoutDashboard className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.dashboard')}</span>
                          </Button>
                        </Link>
                        <Link to="/dashboard/owner/vehicles" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <Car className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.myVehicles')}</span>
                          </Button>
                        </Link>
                        <Link to="/dashboard/owner/fleet" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <Clipboard className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.fleetAvailability')}</span>
                          </Button>
                        </Link>
                        <Link to="/dashboard/owner/bookings" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <Calendar className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.bookings')}</span>
                          </Button>
                        </Link>
                        <Link to="/dashboard/owner/revenue" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <DollarSign className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.revenue')}</span>
                          </Button>
                        </Link>
                        <Link to="/messages" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group relative"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <MessageSquare className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.messages')}</span>
                          </Button>
                        </Link>
                      </>
                    )}

                    {isRenter && (
                      <>
                        <Link to="/search" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <Search className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.search')}</span>
                          </Button>
                        </Link>
                        <Link to="/dashboard/renter/bookings" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <Calendar className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.myBookings')}</span>
                          </Button>
                        </Link>
                        <Link to="/dashboard/renter" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <LayoutDashboard className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.dashboard')}</span>
                          </Button>
                        </Link>
                        <Link to="/messages" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group relative"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <MessageSquare className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.messages')}</span>
                          </Button>
                        </Link>
                        <Link to="/dashboard/renter/cancellations" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <XCircle className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t('nav.myCancellations')}</span>
                          </Button>
                        </Link>
                      </>
                    )}

                    {isAdmin && visibleAdminNav.map((item) => {
                      const Icon = adminNavIcon(item.path);
                      return (
                        <Link key={item.path} to={item.path} onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                          >
                            <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                              <Icon className="h-4 w-4 text-green-night" />
                            </div>
                            <span className="font-medium text-sm">{t(item.labelKey)}</span>
                          </Button>
                        </Link>
                      );
                    })}

                    {/* Séparateur avec style */}
                    <div className="pt-3 border-t border-gray-200 my-3" />

                    {/* Options communes */}
                    <Link to="/profile" onClick={onClose}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                      >
                        <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                          <User className="h-4 w-4 text-green-night" />
                        </div>
                        <span className="font-medium text-sm">{t('common.profile')}</span>
                      </Button>
                    </Link>

                    <Link to="/notifications" onClick={onClose}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group relative"
                      >
                        <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors relative">
                          <Bell className="h-4 w-4 text-green-night" />
                          {unreadCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                          )}
                        </div>
                        <span className="font-medium text-sm">{t('nav.notifications')}</span>
                        {unreadCount > 0 && (
                          <Badge className="ml-auto bg-green-night text-white text-xs font-bold px-2 py-0.5 min-w-[22px] shadow-sm">
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </Badge>
                        )}
                      </Button>
                    </Link>

                    {!isOwner && (
                      <Link to="/agences" onClick={(e) => { onBecomeOwner(e); onClose(); }}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start h-11 px-4 rounded-xl hover:bg-emerald-soft/50 hover:text-green-night transition-all duration-200 group"
                        >
                          <div className="p-1.5 rounded-lg bg-emerald-soft/40 group-hover:bg-emerald-soft/60 mr-3 transition-colors">
                            <Building2 className="h-4 w-4 text-green-night" />
                          </div>
                          <span className="font-medium text-sm">{t('nav.forAgencies')}</span>
                        </Button>
                      </Link>
                    )}

                    {/* Section Aide et Support */}
                    <div className="pt-3 border-t border-gray-200 my-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-4">
                        {t('footer.support')}
                      </p>
                      <div className="space-y-1">
                        <Link to="/faq" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-10 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
                          >
                            <HelpCircle className="h-4 w-4 mr-3 text-gray-500" />
                            <span className="text-sm">{t('footer.faq')}</span>
                          </Button>
                        </Link>
                        <Link to="/contact" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-10 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
                          >
                            <MessageCircle className="h-4 w-4 mr-3 text-gray-500" />
                            <span className="text-sm">{t('footer.contact')}</span>
                          </Button>
                        </Link>
                        <Link to="/about" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-10 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
                          >
                            <Info className="h-4 w-4 mr-3 text-gray-500" />
                            <span className="text-sm">{t('footer.whoWeAre')}</span>
                          </Button>
                        </Link>
                        <Link to="/legal" onClick={onClose}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start h-10 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
                          >
                            <Shield className="h-4 w-4 mr-3 text-gray-500" />
                            <span className="text-sm">{t('footer.terms')}</span>
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  {/* Inscription et Connexion */}
                  <div className="px-5 py-5 border-b border-gray-100">
                    <div className="space-y-4">
                      <Link to="/auth/register" onClick={onClose} className="block">
                        <Button
                          variant="outline"
                          className="w-full border-green-night text-green-night hover:bg-green-night hover:text-white rounded-xl font-semibold shadow-sm hover:shadow-md transition-all duration-200 h-12"
                        >
                          <UserPlus className="mr-2 h-5 w-5" />
                          {t('common.signup')}
                        </Button>
                      </Link>

                      <Link to="/auth/login" onClick={onClose} className="block">
                        <Button
                          variant="default"
                          className="w-full bg-green-night hover:bg-green text-white rounded-xl font-semibold shadow-sm hover:shadow-md transition-all duration-200 h-12"
                        >
                          <LogIn className="mr-2 h-5 w-5" />
                          {t('common.login')}
                        </Button>
                      </Link>
                    </div>

                    {/* Pour Agences - En dessous de Login */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <Link to="/agences" onClick={(e) => { onBecomeOwner(e); onClose(); }} className="block">
                        <Button
                          variant="outline"
                          className="w-full border-green-night text-green-night hover:bg-green-night hover:text-white rounded-xl font-semibold shadow-sm hover:shadow-md transition-all duration-200 h-12"
                        >
                          <Building2 className="mr-2 h-5 w-5" />
                          {t('nav.forAgencies')}
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Section Aide et Support pour non-connectés */}
                  <div className="px-5 py-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      {t('footer.support')}
                    </p>
                    <div className="space-y-1">
                      <Link to="/faq" onClick={onClose}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start h-10 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
                        >
                          <HelpCircle className="h-4 w-4 mr-3 text-gray-500" />
                          <span className="text-sm">{t('footer.faq')}</span>
                        </Button>
                      </Link>
                      <Link to="/contact" onClick={onClose}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start h-10 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
                        >
                          <MessageCircle className="h-4 w-4 mr-3 text-gray-500" />
                          <span className="text-sm">{t('footer.contact')}</span>
                        </Button>
                      </Link>
                      <Link to="/about" onClick={onClose}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start h-10 px-4 rounded-lg hover:bg-gray-50 text-gray-700"
                        >
                          <Info className="h-4 w-4 mr-3 text-gray-500" />
                          <span className="text-sm">{t('footer.whoWeAre')}</span>
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer avec langue et déconnexion - Style amélioré */}
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50/50 space-y-2">
              {user && (
                <Button
                  variant="ghost"
                  className="w-full justify-start h-10 px-4 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
                  onClick={() => {
                    onSignOut();
                    onClose();
                  }}
                >
                  <LogOut className="mr-3 h-4 w-4" />
                  <span className="text-sm font-medium">{t('common.logout')}</span>
                </Button>
              )}
              
              <LanguageSwitcher variant="block" className="pt-1" />
              <CurrencySelector variant="block" />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }
);

MobileMenu.displayName = "MobileMenu";
